import { SearchCheckboxItem } from 'components/SearchCheckboxList';
import { Text } from '@doda/io-react/common';
import { ChangeEvent, FC, Fragment } from 'react';
import { JobCountQuery } from '@doda/common/src/services/getJobCountAPI';
import { useAppSelector } from 'redux/hooks';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { OccupationDrillDown } from 'pages/DodaFront/View/JobSearchList/components/SearchSideBar/DrillDown/OccupationDrillDown';
import { addComma } from '@doda/common/src/utils/convNumberUtil';
import { ConditionsAreaWrapper } from '../../ConditionsAreaWrapper';
import { SelectButton } from '../../SelectButton';
import { TitleWithButton } from '../TitleWithButton';
import styles from './index.module.scss';
import { OccupationType } from '../types';

/**
 * Propsの型定義
 * @param occupation チェックボックス表示用配列
 * @param updateJobCount 求人件数の更新処理
 * @param onChangeIsShowOverlay オーバーレイの表示処理
 */
interface Props {
  occupation: OccupationType;
  updateJobCount: (
    currentCondition?: JobCountQuery,
    isToggleOccupation?: boolean
  ) => Promise<void>;
  onChangeIsShowOverlay: () => void;
  onClickOpenModal: () => void;
}

export const OccupationArea: FC<Props> = ({
  occupation,
  updateJobCount,
  onChangeIsShowOverlay,
  onClickOpenModal,
}) => {
  const { qc } = useAppSelector(appQueryParamsSelector);
  const {
    occupationDisplayList,
    occupationDrillDown,
    updateIsCheckedOccupationLList,
    updateIsCheckedOccupationMList,
    updateIsCheckedOccupationSList,
  } = occupation;

  // 大分類のチェックボックス押下処理
  const onChangeLListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedOccupationLList(event, selectedId);
    updateJobCount(
      { searchJobTypeLId: checkedList, qualificationId: qc },
      true
    );
    onChangeIsShowOverlay();
  };

  // 中分類のチェックボックス押下処理
  const onChangeMListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedOccupationMList(event, selectedId);
    updateJobCount(
      { searchJobTypeMId: checkedList, qualificationId: qc },
      true
    );
    onChangeIsShowOverlay();
  };

  // 小分類のチェックボックス押下処理
  const onChangeSListCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    selectedId: string
  ) => {
    const checkedList = updateIsCheckedOccupationSList(event, selectedId);
    updateJobCount(
      { searchJobTypeSId: checkedList, qualificationId: qc },
      true
    );
    onChangeIsShowOverlay();
  };

  return (
    <ConditionsAreaWrapper>
      <TitleWithButton title="職種">
        <SelectButton title="職種を選択" onClick={onClickOpenModal} />
      </TitleWithButton>
      {occupationDrillDown ? (
        <OccupationDrillDown drillDown={occupationDrillDown} />
      ) : (
        <div className={styles['occupation-check-list']}>
          {occupationDisplayList.map((occupationL) => (
            <Fragment key={occupationL.searchJobTypeId}>
              {occupationL.isDisplay && (
                <SearchCheckboxItem
                  title={occupationL.name}
                  value={occupationL.searchJobTypeId}
                  isChecked={occupationL.isChecked}
                  count={occupationL.jobCount}
                  onChange={(event) =>
                    onChangeLListCheckbox(event, occupationL.searchJobTypeId)
                  }
                />
              )}
              {occupationL.jobTypeMList.map((occupationM) => (
                <Fragment key={occupationM.searchJobTypeId}>
                  {occupationM.isDisplay && (
                    <SearchCheckboxItem
                      title={occupationM.name}
                      isChecked={occupationM.isChecked}
                      value={occupationM.searchJobTypeId}
                      count={occupationM.jobCount}
                      onChange={(event) =>
                        onChangeMListCheckbox(
                          event,
                          occupationM.searchJobTypeId
                        )
                      }
                    />
                  )}
                  {occupationM.jobTypeSList.map((occupationS, index) => (
                    <Fragment key={occupationS.searchJobTypeId}>
                      {occupationS.isDisplay && (
                        <>
                          {/* 親分類が複数表示されてしまうので最初のループのみで表示させる */}
                          {index === 0 && (
                            <div
                              className={
                                styles['occupation-check-list__medium-heading']
                              }
                            >
                              <Text size="12" as="p">
                                {occupationM.name}
                                <Text size="12" color="gray" as="span">
                                  ({addComma(occupationM.jobCount)})
                                </Text>
                              </Text>
                            </div>
                          )}
                          <SearchCheckboxItem
                            key={occupationS.searchJobTypeId}
                            title={occupationS.name}
                            isChecked={occupationS.isChecked}
                            value={occupationS.searchJobTypeId}
                            count={occupationS.jobCount}
                            onChange={(event) =>
                              onChangeSListCheckbox(
                                event,
                                occupationS.searchJobTypeId
                              )
                            }
                          />
                        </>
                      )}
                    </Fragment>
                  ))}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </div>
      )}
    </ConditionsAreaWrapper>
  );
};
